import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { formatWorkTime } from "@/lib/timeFormat";

/**
 * Returns a memoized `format(aw)` function that honours the workshop's
 * `time_display_mode` setting.  Falls back to "aw" mode.
 */
let cachedMode = null;
const subscribers = new Set();

async function refreshMode() {
  try {
    const r = await api.get("/settings");
    const newMode = r.data?.time_display_mode || "hours";
    if (newMode !== cachedMode) {
      cachedMode = newMode;
      subscribers.forEach((cb) => cb(newMode));
    }
  } catch {
    if (cachedMode == null) cachedMode = "hours";
  }
}

export function notifyTimeDisplayModeChanged(mode) {
  cachedMode = mode || "hours";
  subscribers.forEach((cb) => cb(cachedMode));
}

export function useTimeFormat() {
  const { t } = useTranslation();
  const [mode, setMode] = useState(cachedMode || "hours");
  useEffect(() => {
    if (cachedMode == null) refreshMode();
    const cb = (m) => setMode(m);
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  }, []);
  return {
    mode,
    format: (aw) => formatWorkTime(aw, mode, t),
  };
}
